import React, { memo, useCallback, useMemo } from 'react';
import { Form, Radio, Typography } from 'antd';
import { useBundle } from '@amzn/react-arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';
import type { NamePath } from '@xbcb/ui-types';
import { TariffAdditionalInfoFieldType } from '@xbcb/trade-data-types';
import type { ModeOfTransport } from '__generated__/graphql';
import { useTariffEvaluation } from 'components/LineItemTariffDisplayV2/useTariffEvaluation';
import { getMergedQuestions, type ProductUsageQuestion } from './config';
import { AnswerRow, LoadingSpinner, QuestionBlock } from './styles';

interface CandidateAdditionalTariff {
  number: string;
  type?: string | null;
}

export interface ProductUsageTabProps {
  fullNamePath: NamePath;
  candidateAdditionalTariffs: CandidateAdditionalTariff[];
  originCountryCode: string | undefined;
  arrivalDate: string | undefined;
  modeOfTransport: ModeOfTransport | undefined;
  readOnly?: boolean;
}

export const ProductUsageTab: React.FC<ProductUsageTabProps> = ({
  fullNamePath,
  candidateAdditionalTariffs,
  originCountryCode,
  arrivalDate,
  modeOfTransport,
  readOnly,
}) => {
  const [bundle] = useBundle('components.ProductUsageTab');
  const form = Form.useFormInstance();
  const lineFullNamePath = fullNamePath as (string | number)[];

  const productCharacteristics = Form.useWatch(
    [...lineFullNamePath, 'productCharacteristics'],
    form,
  );

  const questions: ProductUsageQuestion[] = useMemo(() => {
    const line = form.getFieldValue(lineFullNamePath) ?? {};
    return getMergedQuestions(
      line,
      candidateAdditionalTariffs.map((c) => c.type),
    );
  }, [
    candidateAdditionalTariffs,
    form,
    lineFullNamePath,
    productCharacteristics,
  ]);

  const tariffFullNamePath = useMemo(
    () => [...lineFullNamePath, 'tariffs'],
    [lineFullNamePath],
  );

  const { runTariffEvaluation, evaluating } = useTariffEvaluation({
    form,
    tariffFullNamePath,
    originCountryCode,
    arrivalDate,
    modeOfTransport,
  });

  // Drive Radio.Group values directly from form state. All questions
  // currently live under `productCharacteristics`; the watcher above
  // (`productCharacteristics`) re-renders this component on each answer.
  const getAnswer = useCallback(
    (q: ProductUsageQuestion): boolean | undefined => {
      const stored = form.getFieldValue([...lineFullNamePath, ...q.entryPath]);
      if (stored === undefined || stored === null) return undefined;
      return q.invert ? !stored : stored;
    },
    [form, lineFullNamePath],
  );

  const answeredCount = useMemo(
    () => questions.filter((q) => getAnswer(q) !== undefined).length,
    // productCharacteristics is watched above; re-runs trigger re-render
    [questions, getAnswer, productCharacteristics],
  );
  const visibleCount = evaluating
    ? answeredCount
    : Math.min(answeredCount + 1, questions.length);

  const handleChange = useCallback(
    (question: ProductUsageQuestion) => (value: boolean) => {
      const namePath = [...lineFullNamePath, ...question.entryPath];
      form.setFieldValue(namePath, question.invert ? !value : value);

      const tariffs = form.getFieldValue([...lineFullNamePath, 'tariffs']);
      const primaryHts = tariffs?.[tariffs.length - 1]?.htsNumber;
      if (primaryHts) {
        void runTariffEvaluation(primaryHts);
      }
    },
    [form, lineFullNamePath, runTariffEvaluation],
  );

  return (
    <div>
      {questions.slice(0, visibleCount).map((question) => (
        <QuestionBlock key={question.fieldPath}>
          <Typography.Text>
            {safeGetMessage(
              bundle,
              question.messageKey,
              undefined,
              question.fallback,
            )}
          </Typography.Text>
          <AnswerRow>
            {question.fieldType === TariffAdditionalInfoFieldType.BOOLEAN && (
              <Radio.Group
                value={getAnswer(question)}
                onChange={(e) => handleChange(question)(e.target.value)}
                disabled={readOnly}
              >
                <Radio value={true}>
                  {safeGetMessage(bundle, 'yes', undefined, 'Yes')}
                </Radio>
                <Radio value={false}>
                  {safeGetMessage(bundle, 'no', undefined, 'No')}
                </Radio>
              </Radio.Group>
            )}
          </AnswerRow>
        </QuestionBlock>
      ))}
      {evaluating && <LoadingSpinner size="small" />}
    </div>
  );
};

export default memo(ProductUsageTab);
